/**
 * Global handling for a session that dies in place (BAN-5947).
 *
 * The route guard only runs on navigation, so a mounted tab whose Clerk
 * session ends never redirects on its own — it keeps refetching on focus
 * with no Authorization header and the API answers 401 forever. The server
 * is the authority on authentication: any 401 received while Clerk still
 * believes a user is signed in means the session is dead. We end it once
 * and hard-redirect to /login, which unloads the page and stops all
 * polling by construction.
 *
 * Anonymous 401s on guest surfaces (e.g. an expired guest preview link on
 * /join) pass through to their callers. Everywhere else a 401 ends the
 * session even when Clerk has already cleared its client state — clerk-js
 * can sign out in place without the app ever navigating, and that parked
 * tab loops on 401s exactly like the zombie one.
 */
import type { AxiosInstance } from 'axios';
import { isAxiosError } from 'axios';

// Routes where anonymous visitors legitimately receive 401s (tokenized
// links that can expire). A 401 from any other path means this tab was
// authenticated.
const GUEST_PATH_PREFIXES = [
  '/login',
  '/join',
  '/email-response',
  '/events/rsvp',
  '/registration',
];

const isGuestPath = (pathname: string): boolean =>
  GUEST_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

// The session is already dead server-side, so a Clerk sign-out that hangs
// (or fails) must not block the redirect.
const SIGN_OUT_TIMEOUT_MS = 2500;

let endingSession = false;

const loginUrl = (): string => {
  const continuePath = location.pathname + location.search + location.hash;
  const meaningful =
    continuePath.startsWith('/') &&
    continuePath.length > 1 &&
    !continuePath.startsWith('/login');
  return meaningful
    ? `/login?continue=${encodeURIComponent(continuePath)}`
    : '/login';
};

export const endDeadSession = async (): Promise<void> => {
  if (endingSession) return;
  endingSession = true;
  // Sign out only the session this tab believes in. An unscoped signOut()
  // ends the client's LIVE session, so a parked tab waking up on a dead one
  // would revoke the fresh session the user just created in another tab —
  // a sign-in/kick-out loop. No session in client state = nothing to end.
  const deadSessionId = window.Clerk?.session?.id;
  if (deadSessionId) {
    try {
      await Promise.race([
        window.Clerk?.signOut({ sessionId: deadSessionId }),
        new Promise((resolve) => setTimeout(resolve, SIGN_OUT_TIMEOUT_MS)),
      ]);
    } catch {
      // Sign-out failure is expected when the session is already gone.
    }
  }
  location.assign(loginUrl());
};

export const installDeadSessionInterceptor = (
  instance: AxiosInstance,
): (() => void) => {
  const interceptorId = instance.interceptors.response.use(
    undefined,
    (error: unknown) => {
      if (
        isAxiosError(error) &&
        error.response?.status === 401 &&
        (window.Clerk?.user || !isGuestPath(location.pathname))
      ) {
        void endDeadSession();
      }
      return Promise.reject(error);
    },
  );
  return () => instance.interceptors.response.eject(interceptorId);
};

export const resetDeadSessionStateForTests = (): void => {
  endingSession = false;
};
